import { useEffect, useRef, useState, type CSSProperties } from "react";
import {
  CheckCircle2,
  Dices,
  Film,
  Play,
  RotateCcw,
  Search,
  Sparkles,
  X
} from "lucide-react";
import {
  MOVIE_GACHA_CANDIDATES,
  MOVIE_GACHA_DEFAULT_RESULT_INDEX,
  type MovieGachaCandidate
} from "../../config/movieGachaDemo";
import { assetUrl } from "../../utils/assetUrl";
import "./NightMovieGachaDemo.css";

const PROMPT_DELAY_MS = 1_200;
const PROMPT_TIMEOUT_MS = 15_000;
const SPIN_DURATION_MS = 3_250;

type MovieGachaStage = "home" | "machine" | "spinning" | "reveal" | "accepted";
type CapsuleStyle = CSSProperties & Record<`--capsule-${string}`, string>;

const CAPSULE_POSITIONS = [
  { x: 18, y: 24, rotate: -18, delay: -0.2 },
  { x: 42, y: 12, rotate: 10, delay: -0.52 },
  { x: 67, y: 25, rotate: 21, delay: -0.86 },
  { x: 26, y: 52, rotate: 12, delay: -1.12 },
  { x: 54, y: 48, rotate: -13, delay: -1.38 },
  { x: 72, y: 57, rotate: 18, delay: -1.66 },
  { x: 46, y: 70, rotate: -8, delay: -1.94 }
] as const;

export function NightMovieGachaDemo({
  evaSrc,
  onClose
}: {
  evaSrc: string;
  onClose: () => void;
}) {
  const timersRef = useRef<Set<number>>(new Set());
  const spinCountRef = useRef(0);
  const [promptVisible, setPromptVisible] = useState(false);
  const [stage, setStage] = useState<MovieGachaStage>("home");
  const [selectedIndex, setSelectedIndex] = useState(MOVIE_GACHA_DEFAULT_RESULT_INDEX);
  const selectedMovie = MOVIE_GACHA_CANDIDATES[selectedIndex];

  useEffect(() => {
    const showTimer = window.setTimeout(() => setPromptVisible(true), PROMPT_DELAY_MS);
    const hideTimer = window.setTimeout(() => setPromptVisible(false), PROMPT_DELAY_MS + PROMPT_TIMEOUT_MS);
    timersRef.current.add(showTimer);
    timersRef.current.add(hideTimer);

    return () => {
      timersRef.current.forEach((timerId) => window.clearTimeout(timerId));
      timersRef.current.clear();
    };
  }, []);

  function openMachine() {
    setPromptVisible(false);
    setStage("machine");
  }

  function startSpin() {
    if (stage === "spinning") {
      return;
    }

    const nextIndex = spinCountRef.current === 0
      ? MOVIE_GACHA_DEFAULT_RESULT_INDEX
      : (selectedIndex + 3) % MOVIE_GACHA_CANDIDATES.length;

    spinCountRef.current += 1;
    setSelectedIndex(nextIndex);
    setStage("spinning");

    const timerId = window.setTimeout(() => {
      timersRef.current.delete(timerId);
      setStage("reveal");
    }, SPIN_DURATION_MS);
    timersRef.current.add(timerId);
  }

  function resetMachine() {
    setStage("machine");
  }

  return (
    <section className="night-movie-gacha" aria-label="选片扭蛋机演示">
      <MovieHomeHeader onClose={onClose} />

      <main className="movie-gacha-home" aria-label="视频首页">
        <section className="movie-gacha-home__featured" aria-label="正在热播">
          {MOVIE_GACHA_CANDIDATES.slice(0, 3).map((movie, index) => (
            <MovieHomeTile key={movie.id} movie={movie} size={index === 0 ? "hero" : "feature"} />
          ))}
        </section>

        <section className="movie-gacha-home__rail" aria-label="热门力荐">
          <header>
            <h2>热门力荐</h2>
            <span>根据近期观看偏好更新</span>
          </header>
          <div>
            {MOVIE_GACHA_CANDIDATES.slice(3).map((movie) => (
              <MovieHomeTile key={movie.id} movie={movie} size="compact" />
            ))}
          </div>
        </section>
      </main>

      {promptVisible && stage === "home" ? (
        <button className="movie-gacha-prompt" type="button" onClick={openMachine}>
          <img src={evaSrc} alt="" draggable={false} />
          <span>
            <small>EVA</small>
            <strong>不知道看什么，我帮你一起挑吧</strong>
          </span>
          <em>一起挑</em>
        </button>
      ) : null}

      {stage !== "home" ? (
        <div className="movie-gacha-overlay">
          <button
            className="movie-gacha-overlay__close"
            type="button"
            onClick={() => setStage("home")}
            aria-label="关闭选片扭蛋机"
          >
            <X size={30} />
          </button>

          <header className="movie-gacha-overlay__header">
            <span><Sparkles size={24} /> EVA 选片实验室</span>
            <h1>选片扭蛋机</h1>
            <p>把今晚的选择，交给一点随机。</p>
          </header>

          <div className={`movie-gacha-stage movie-gacha-stage--${stage}`}>
            <GachaMachine
              selectedMovie={selectedMovie}
              spinning={stage === "spinning"}
              onSpin={startSpin}
            />

            {stage === "reveal" || stage === "accepted" ? (
              <MovieGachaResult
                accepted={stage === "accepted"}
                movie={selectedMovie}
                onAccept={() => setStage("accepted")}
                onRetry={resetMachine}
              />
            ) : (
              <aside className="movie-gacha-instruction" aria-live="polite">
                <div>
                  <strong>{stage === "spinning" ? "正在打乱今晚的片单" : "转动扭蛋，随机挑一部"}</strong>
                  <p>{stage === "spinning" ? "正在综合你的偏好与片单热度…" : "七个候选内容已经放进扭蛋机。"}</p>
                </div>
              </aside>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}

function MovieHomeHeader({ onClose }: { onClose: () => void }) {
  const categories = ["推荐", "少儿", "精选", "电影", "电视剧", "动漫", "综艺", "纪录片"];

  return (
    <header className="movie-gacha-appbar">
      <nav aria-label="视频分类">
        {categories.map((category, index) => (
          <button key={category} className={index === 0 ? "is-active" : ""} type="button">
            {category}
          </button>
        ))}
      </nav>
      <div className="movie-gacha-appbar__tools">
        <button type="button" aria-label="搜索影片"><Search size={26} /></button>
        <span>20:15</span>
        <button type="button" onClick={onClose} aria-label="关闭选片扭蛋机演示"><X size={28} /></button>
      </div>
    </header>
  );
}

function MovieHomeTile({
  movie,
  size
}: {
  movie: MovieGachaCandidate;
  size: "hero" | "feature" | "compact";
}) {
  return (
    <article className={`movie-gacha-home-tile movie-gacha-home-tile--${size}`}>
      <img src={assetUrl(movie.imageSrc)} alt="" draggable={false} />
      <span className="movie-gacha-home-tile__shade" aria-hidden="true" />
      <div>
        <em>{movie.badge}</em>
        <strong>{movie.title}</strong>
        <small>{movie.subtitle}</small>
      </div>
    </article>
  );
}

function GachaMachine({
  selectedMovie,
  spinning,
  onSpin
}: {
  selectedMovie: MovieGachaCandidate;
  spinning: boolean;
  onSpin: () => void;
}) {
  return (
    <div className={`movie-gacha-machine${spinning ? " movie-gacha-machine--spinning" : ""}`}>
      <div className="movie-gacha-machine__halo" aria-hidden="true" />
      <div className="movie-gacha-machine__dome">
        <div className="movie-gacha-machine__glass" aria-hidden="true" />
        <div className="movie-gacha-machine__capsules" aria-hidden="true">
          {MOVIE_GACHA_CANDIDATES.map((movie, index) => {
            const position = CAPSULE_POSITIONS[index];
            const style = {
              "--capsule-x": `${position.x}%`,
              "--capsule-y": `${position.y}%`,
              "--capsule-rotate": `${position.rotate}deg`,
              "--capsule-delay": `${position.delay}s`
            } as CapsuleStyle;

            return (
              <span
                key={movie.id}
                className={`movie-gacha-capsule movie-gacha-capsule--${movie.capsuleTone}`}
                style={style}
              >
                <img src={assetUrl(movie.imageSrc)} alt="" draggable={false} />
              </span>
            );
          })}
        </div>
      </div>

      <div className="movie-gacha-machine__body">
        <div className="movie-gacha-machine__display">
          <Film size={23} />
          <span>{spinning ? "正在选片" : "7 部候选内容"}</span>
        </div>
        <div className="movie-gacha-machine__chute">
          {spinning ? (
            <span className={`movie-gacha-capsule movie-gacha-capsule--drop movie-gacha-capsule--${selectedMovie.capsuleTone}`}>
              <img src={assetUrl(selectedMovie.imageSrc)} alt="" draggable={false} />
            </span>
          ) : null}
        </div>
        <button className="movie-gacha-machine__handle" type="button" onClick={onSpin} disabled={spinning}>
          <Dices size={34} />
          <span>{spinning ? "转动中" : "扭一下"}</span>
        </button>
      </div>
    </div>
  );
}

function MovieGachaResult({
  accepted,
  movie,
  onAccept,
  onRetry
}: {
  accepted: boolean;
  movie: MovieGachaCandidate;
  onAccept: () => void;
  onRetry: () => void;
}) {
  return (
    <article className="movie-gacha-result" aria-live="polite">
      <div className="movie-gacha-result__artwork">
        <img src={assetUrl(movie.imageSrc)} alt="" draggable={false} />
        <span>{movie.badge}</span>
      </div>
      <div className="movie-gacha-result__copy">
        <small><Sparkles size={19} /> EVA 为你扭到</small>
        <h2>{movie.title}</h2>
        <p>{movie.subtitle}</p>
        <blockquote>“{movie.recommendation}”</blockquote>
        {accepted ? (
          <div className="movie-gacha-result__accepted">
            <CheckCircle2 size={24} />
            已选好，准备为你播放
          </div>
        ) : (
          <div className="movie-gacha-result__actions">
            <button type="button" onClick={onAccept}><Play size={22} fill="currentColor" /> 就看这个</button>
            <button type="button" onClick={onRetry}><RotateCcw size={22} /> 再扭一次</button>
          </div>
        )}
      </div>
    </article>
  );
}
